import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Forgot Password — collects an email, hits POST /api/auth/forgot-password,
// then shows a generic "check your email" confirmation. The backend ALWAYS
// returns 200 whether the email exists or not (enumeration-safe), so this
// UI never reveals whether an account was found. Same success screen either
// way. The user gets a Resend email with a single-use reset link.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-[80vh] flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#C9A85F]">→ Book Away</p>
        <h1 className="mt-2 text-3xl md:text-4xl leading-tight tracking-tight">Forgot your password?</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Enter your Book Away email and we'll send you a link to reset it. The link stays valid for 1 hour.
        </p>

        {sent ? (
          <div
            data-testid="forgot-password-sent"
            className="mt-8 border border-[#C9A85F] bg-[#C9A85F]/5 p-6"
          >
            <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#8A7333]">→ Check your inbox</p>
            <p className="mt-2 text-sm">
              If <span className="font-medium">{email}</span> is registered with Book Away, a password
              reset link is on its way. Please check your inbox (and spam) in the next few minutes.
            </p>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              className="mono mt-4 inline-block text-[11px] uppercase tracking-widest text-[#8A7333] hover:text-[#C9A85F]"
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email" className="mono text-[10px] uppercase tracking-[0.2em]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="forgot-password-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none"
                placeholder="you@example.com"
              />
            </div>
            {error && (
              <p data-testid="forgot-password-error" className="mono text-xs text-[#F87171]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="forgot-password-submit-btn"
              disabled={busy}
              className="mt-2 w-full bg-foreground text-background px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#C9A85F] active:scale-[0.98] transition-colors disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <Link
              to="/login"
              data-testid="forgot-password-cancel"
              className="mono text-center text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              ← Back to sign in
            </Link>
          </form>
        )}
      </div>
    </section>
  );
}
